import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { TicketService } from 'src/app/private/services/ticket.service';
import { UsersService } from 'src/app/private/services/users.service';
import { LocalStorageService } from 'src/app/utils/LocalStorageService';
import { LeadActivityFeedItem, TicketCardData } from '../lead-activity-feed/lead-activity-feed.component';

@Component({
  selector: 'app-lead-profile-tickets',
  templateUrl: './lead-profile-tickets.component.html',
  styleUrls: ['./lead-profile-tickets.component.scss'],
})
export class LeadProfileTicketsComponent implements OnInit, OnChanges {
  @Input() leadId: number | string | null = null;
  @Input() refreshToken = 0;
  @Output() changed = new EventEmitter<void>();

  @ViewChild('createTicketTpl') createTicketTpl!: TemplateRef<unknown>;
  @ViewChild('deleteTpl') deleteTpl!: TemplateRef<unknown>;

  items: LeadActivityFeedItem[] = [];
  loading = false;
  btnLoading = false;
  users: any[] = [];
  formAction = 'Create';
  currentTab = 'Lead Profile';
  selectedTicket: any = null;

  constructor(
    private ticketApi: TicketService,
    private usersApi: UsersService,
    private localStorage: LocalStorageService,
    private modalService: NgbModal,
    private toast: ToastrService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['leadId'] || changes['refreshToken']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.loadUsers();
  }

  loadUsers(): void {
    this.usersApi
      .findAllUsers({ client_id: this.localStorage.getClientId() })
      .subscribe({
        next: (res: any) => {
          this.users = res || [];
        },
        error: () => {
          this.users = [];
        },
      });
  }

  load(): void {
    if (this.leadId == null || this.leadId === '') {
      this.items = [];
      return;
    }
    this.loading = true;
    this.ticketApi
      .getAllTinaTicket({
        page: 0,
        limit: 50,
        where: {
          client_id: this.localStorage.getClientId(),
          lead_id: this.leadId,
        },
        order: {
          col: '"manage_ticket"."createdAt"',
          dir: 'DESC',
        },
      })
      .subscribe({
        next: (res: any) => {
          const rows = res?.rows ?? [];
          this.items = rows.map((row: any) => {
            const ticketData: TicketCardData = {
              ticketNo: String(row.ticket_id || row.id || ''),
              title: row.title || 'Untitled',
              status: row.ticket_status || row.status || '',
              priority: row.priority || '',
              source: row.source || '',
              description: row.description || '',
            };
            return {
              id: row.id,
              actorName: row.ticket_status || 'Ticket',
              createdAt: row.createdAt,
              body: '',
              ticketData,
              raw: row,
            };
          });
          this.loading = false;
        },
        error: () => {
          this.items = [];
          this.loading = false;
        },
      });
  }

  onAddTicket(): void {
    if (!this.createTicketTpl) {
      return;
    }
    this.formAction = 'Create';
    this.selectedTicket = null;
    this.modalService.open(this.createTicketTpl, {
      size: 'lg',
      scrollable: true,
      centered: true,
    });
  }

  onEditTicket(item: LeadActivityFeedItem): void {
    if (!this.createTicketTpl || !item.raw) {
      return;
    }
    this.formAction = 'Update';
    this.selectedTicket = item.raw;
    this.modalService.open(this.createTicketTpl, {
      size: 'lg',
      scrollable: true,
      centered: true,
    });
  }

  onDeleteTicket(item: LeadActivityFeedItem): void {
    if (!this.deleteTpl || !item.raw) {
      return;
    }
    this.selectedTicket = item.raw;
    this.modalService.open(this.deleteTpl, {
      size: 'md',
      centered: true,
    });
  }

  confirmDelete(modal: { dismiss: () => void }): void {
    if (!this.selectedTicket) {
      return;
    }
    this.btnLoading = true;
    this.ticketApi
      .updateTicket({
        ...this.selectedTicket,
        ticket_status: 'Deleted',
        updated_by: {
          user_id: this.localStorage.getUserId(),
          name: this.localStorage.getProfileName(),
          email: this.localStorage.getEmail(),
        },
      })
      .subscribe({
        next: () => {
          this.btnLoading = false;
          modal.dismiss();
          this.toast.success('Ticket deleted');
          this.load();
          this.changed.emit();
        },
        error: (err: any) => {
          this.btnLoading = false;
          this.toast.error(err?.error?.message || 'Could not delete ticket');
        },
      });
  }

  onTicketSaved(modal: { dismiss: () => void }): void {
    modal.dismiss();
    this.load();
    this.changed.emit();
  }

  onTicketClosed(modal: { dismiss: () => void }): void {
    modal.dismiss();
  }
}
